import * as MD from '@material-ui/core';
import MuiTextField from '@material-ui/core/TextField';
import * as React from 'react';
import PasswordStrengthBar from 'react-password-strength-bar';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { Dispatch } from 'redux';
import styled from 'styled-components';
import zxcvbn from 'zxcvbn';
import { IApplicationState, IConnectedReduxProps } from '../../Store';
import { I18N } from '../../Store/I18n/Types';
import {
	updatePasswordFromEmail,
	updatePasswordFromPrevious,
} from '../../Store/UpdatePassword/Actions';
import { IUpdatePasswordState } from '../../Store/UpdatePassword/Types';
import { initialUserInfo } from '../../Store/User/Reducer';
import { getTheme } from '../../Themes';
import { TypedStorage } from '../../Utils/TypedStorage';
import * as PwdGeneral from '../Password/PasswordGeneral';

interface IPropsFromState {
	i18n: I18N;
	updatePassword: IUpdatePasswordState;
}

interface IState {
	errorPassword: boolean;
	passwordIdentical: boolean;
	errorPasswordMsg: string;
	newPassword1: string;
	newPassword2: string;
	newPwdInvalidList: Array<string>;
	previousPassword: string;
	strength: number;
}

interface IPropsFromDispatch {
	updatePasswordFromEmail: typeof updatePasswordFromEmail;
	updatePasswordFromPrevious: typeof updatePasswordFromPrevious;
}

type AllProps = MD.WithStyles<typeof styles> &
	IPropsFromState &
	IPropsFromDispatch &
	RouteComponentProps<{}> &
	IConnectedReduxProps;

const initialState = {
	errorPassword: false,
	passwordIdentical: true,
	errorPasswordMsg: '',
	newPassword1: '',
	newPassword2: '',
	newPwdInvalidList: [],
	previousPassword: '',
	strength: 0,
};

const theme = getTheme();

const styles = MD.createStyles({
	button: {
		margin: theme.spacing(1),
		marginLeft: 0,
		marginRight: 0,
		textTransform: 'uppercase',
	},
	card: {
		maxWidth: '40%',
		minWidth: '20%',
		marginTop: '50px',
	},
	cardContent: {
		width: '100%',
	},
	errorContainer: {
		width: '100%',
		justifyContent: 'center',
		textAlign: 'center',
		color: 'red',
	},
	form: {
		display: 'flex',
		flexDirection: 'column',
		flexWrap: 'wrap',
	},
	media: {
		height: 140,
	},
});

const DensedTextField = styled(MuiTextField)`
	.MuiInputBase-input {
		padding: 4px 6px;
		font-size: 13px;
	}
	.MuiInputLabel-formControl {
		top: -7px;
		font-size: 13px;
		font-weight: 300;
	}
`;

class UpdatePasswordComponent extends React.Component<AllProps, IState> {
	private readonly urlParams = new URLSearchParams(window.location.search);
	private readonly myToken = this.urlParams.get('token');
	private readonly myID = this.urlParams.get('id');
	private readonly minStrength = 1;
	private readonly initialUserToken = TypedStorage.get('userToken', '');
	private readonly initialUserInfo = TypedStorage.get(
		'userInfo',
		initialUserInfo
	);

	public constructor(props: AllProps) {
		super(props);
		this.state = {
			...initialState,
		};
	}

	public render(): React.ReactNode {
		if (this.myToken === null) {
		}
		return (
			<div>
				{this.props.updatePassword.isUpdateSucceed
					? 'Votre mot de passe a été mis à jour, vous pouvez fermer la fenêtre'
					: this.getForm()}
			</div>
		);
	}

	private getForm(): React.ReactNode {
		const { classes } = this.props;

		return (
			<form
				className={classes.form}
				noValidate={true}
				onSubmit={this.onSubmit.bind(this)}
			>
				<DensedTextField
					autoComplete="new-password"
					margin="normal"
					label={this.props.i18n._('New Password')}
					onChange={this.onNewPassword1Change.bind(this)}
					type="password"
					value={this.state.newPassword1}
					error={this.state.errorPassword}
				/>
				{this.getStrenghBar()}
				{!this.state.passwordIdentical ? (
					<MD.Grid className={classes.errorContainer}>
						<span>Les mots de passes doivent être identiques</span>
					</MD.Grid>
				) : (
					''
				)}
				<DensedTextField
					autoComplete="new-password"
					margin="normal"
					error={this.state.errorPassword}
					label={this.props.i18n._('Confirm password')}
					onChange={this.onNewPassword2Change.bind(this)}
					type="password"
					value={this.state.newPassword2}
					onKeyPress={(e: React.KeyboardEvent<HTMLDivElement>) => {
						if (e.key === 'Enter') {
							this.onSubmit();
						}
					}}
				/>
				{this.state.errorPassword ? (
					<MD.Grid className={classes.errorContainer}>
						{this.getInvalidPasswordMsg()}
					</MD.Grid>
				) : (
					''
				)}
				<MD.Button
					className={classes.button}
					color="primary"
					variant="contained"
					onClick={this.onSubmit.bind(this)}
				>
					{this.props.i18n._('Reset')}
				</MD.Button>
				{this.props.updatePassword.updateIsErrorMsg !== '' ? (
					<MD.Grid className={classes.errorContainer}>
						{this.props.updatePassword.updateIsErrorMsg}
					</MD.Grid>
				) : (
					''
				)}
			</form>
		);
	}

	private getStrenghBar(): React.ReactElement {
		let a = (
			<PasswordStrengthBar
				password={this.state.newPassword1}
				scoreWords={['faible', 'faible', 'moyen', 'bon', 'fort']}
				shortScoreWord={'trop court'}
				minLength={8}
			/>
		);
		return a;
	}

	private onNewPassword1Change(ev: React.ChangeEvent<HTMLInputElement>) {
		this.passwordCheckGeneral(
			this.state.newPassword2,
			ev.target.value,
			7,
			this.minStrength
		);
		this.setState({ newPassword1: ev.target.value });
		this.setState({ strength: zxcvbn(ev.target.value).score });
	}

	private onNewPassword2Change(ev: React.ChangeEvent<HTMLInputElement>) {
		this.passwordCheckGeneral(
			this.state.newPassword1,
			ev.target.value,
			7,
			this.minStrength
		);
		this.setState({ newPassword2: ev.target.value });
		this.setState({ strength: zxcvbn(ev.target.value).score });
	}

	private passwordChecker(): boolean {
		const passwordValid = this.passwordCheckGeneral(
			this.state.newPassword1,
			this.state.newPassword2,
			7,
			this.minStrength
		);
		return passwordValid;
	}

	private passwordCheckGeneral(
		p1: string,
		p2: string,
		lengthmin: number,
		strengthmin: number
	): boolean {
		const passwordValid = PwdGeneral.passwordChecker(
			p1,
			p2,
			lengthmin,
			strengthmin
		);
		const isSamePwd = PwdGeneral.passwordStrictIdentical(p1, p2);
		if (passwordValid) {
			this.setState({ errorPassword: false });
		} else {
			this.setState({ errorPassword: true });
		}
		if (isSamePwd) {
			this.setState({ passwordIdentical: true });
		} else {
			this.setState({ passwordIdentical: false });
		}

		return passwordValid;
	}

	private getInvalidPasswordMsg(): JSX.Element[] {
		const errorListing = PwdGeneral.passwordIndication(
			this.state.newPassword1
		);
		const listItems = errorListing.map((errorListing) => (
			<p>{errorListing}</p>
		));
		return listItems;
	}

	private onSubmit() {
		if (
			this.myToken !== null &&
			this.myID !== null &&
			this.passwordChecker()
		) {
			this.props.updatePasswordFromEmail(
				this.state.newPassword1,
				this.myToken,
				this.myID
			);
		}
	}

	private onSubmitPrevious() {
		if (this.initialUserToken !== null && this.passwordChecker()) {
			this.props.updatePasswordFromPrevious(
				this.state.previousPassword,
				this.state.newPassword1,
				this.initialUserToken,
				this.initialUserInfo.id.toString()
			);
		}
	}
}

const mapStateToProps = ({
	i18n,
	layout,
	notificationBar,
	updatePassword,
}: IApplicationState) => ({
	i18n: i18n.i18n,
	layout,
	notificationBar,
	updatePassword,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
	updatePasswordFromEmail: (p: string, t: string, i: string) =>
		dispatch(updatePasswordFromEmail(p, t, i)),
	updatePasswordFromPrevious: (
		pp: string,
		np: string,
		t: string,
		i: string
	) => dispatch(updatePasswordFromPrevious(pp, np, t, i)),
});

export const AskUpdatePassword = connect(
	mapStateToProps,
	mapDispatchToProps
)(MD.withStyles(styles)(UpdatePasswordComponent));
